import { Printer } from "lucide-react";

import { formatVnd } from "@/shared/format";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const NATIVE_SELECT = "h-9 w-full rounded-md border border-input bg-background px-3 text-sm";

/** The clinic services this panel can offer and label. */
export interface OrderableService {
  id: string;
  name: string;
  active: boolean;
  serviceType: string;
  priceVnd: number | null;
  category: string | null;
}

/**
 * Chỉ định CLS / dịch vụ — the paraclinical orders the doctor writes (TD-7b, #18800).
 *
 * <p><b>Owns the list, not the string.</b> The orders arrive as a `string[]` and leave as a
 * `string[]`; the workspace serialises them into the SOAP note. That split is deliberate and load
 * bearing: the stored form is `"Chỉ định: a; b"`, and the BACKEND parses it back out in
 * `EncounterDirectory.orderedServiceNames` (TD-5d, #18798) to build the invoice's service lines.
 * If that serialisation lived here, this panel would silently own a cross-system contract with
 * billing. It is pinned by a test on each side of the wire.
 *
 * <p>Printing a phiếu is delegated upward — the slip carries patient and doctor identity, which an
 * orders panel has no business holding (§9).
 */
export function OrdersPanel({
  orders,
  onChange,
  services,
  onPrintSlip,
  readOnly,
}: {
  orders: string[];
  onChange: (next: string[]) => void;
  services: OrderableService[];
  onPrintSlip: (serviceName: string) => void;
  readOnly: boolean;
}) {
  function add(name: string) {
    if (!name || orders.includes(name)) return;
    onChange([...orders, name]);
  }
  function remove(name: string) {
    onChange(orders.filter((s) => s !== name));
  }
  /** Phòng thực hiện, taken from the service's category. */
  const serviceRoomLabel = (orderName: string): string =>
    services.find((s) => s.name === orderName)?.category?.trim() || "—";

  return (
    <div className="space-y-3">
      {!readOnly && (
        <select
          className={NATIVE_SELECT}
          value=""
          onChange={(ev) => {
            add(ev.target.value);
            ev.currentTarget.value = "";
          }}
        >
          <option value="">+ Thêm chỉ định (X-quang, siêu âm…)</option>
          {services
            .filter((s) => s.active && s.serviceType === "SERVICE")
            .map((s) => (
              <option key={s.id} value={s.name}>
                {s.name}
                {s.priceVnd != null ? ` — ${formatVnd(s.priceVnd)}` : ""}
              </option>
            ))}
        </select>
      )}
      {orders.length > 0 ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">STT</TableHead>
                <TableHead>Tên chỉ định</TableHead>
                <TableHead className="w-32">Phòng thực hiện</TableHead>
                <TableHead className="w-32 text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((s, i) => (
                <TableRow key={s}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{s}</TableCell>
                  <TableCell>{serviceRoomLabel(s)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          onPrintSlip(s)
                        }
                      >
                        <Printer className="mr-1 h-4 w-4" /> In phiếu
                      </Button>
                      {!readOnly && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => remove(s)}
                        >
                          Xoá
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Chưa có chỉ định. Dịch vụ được thu phí ở bước Hoá đơn.
        </p>
      )}
    </div>
  );
}
